import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type DocumentTopicJSON = z.infer<typeof DocumentTopicSchema.DocumentTopicJSONZ>;
export type DocumentTopicParams = z.infer<typeof DocumentTopicSchema.DocumentTopicZ>;

export default class DocumentTopicSchema {
  public static readonly DocumentTopicJSONZ = z.object({
    entityClass: z.string().nullish(),
    entityId: z.uuid().nullish(),
    context: z.string().nullish(),
  });
  public static readonly DocumentTopicZ = z.object({
    entityClass: z.string().optional(),
    entityId: z.instanceof(Guid).optional(),
    context: z.string().optional(),
  });

  public static validate(values: unknown): DocumentTopicParams {
    return parseOrThrow(DocumentTopicSchema.DocumentTopicZ, values, {
      logError: true,
      schemaName: "DocumentTopicSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): DocumentTopicJSON {
    return parseOrThrow(DocumentTopicSchema.DocumentTopicJSONZ, values, {
      logError: true,
      schemaName: "DocumentTopicSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is DocumentTopicParams {
    try {
      DocumentTopicSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is DocumentTopicJSON {
    try {
      DocumentTopicSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: DocumentTopicJSON): DocumentTopicParams {
    const parsed = parseOrThrow(DocumentTopicSchema.DocumentTopicJSONZ, data, {
      logError: true,
      schemaName: "DocumentTopicSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return DocumentTopicSchema.codec.decode(parsed);
  }

  public static serialize(topic: DocumentTopicParams): DocumentTopicJSON {
    const parsed = DocumentTopicSchema.validate(topic);
    return DocumentTopicSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(DocumentTopicSchema.DocumentTopicJSONZ, DocumentTopicSchema.DocumentTopicZ, {
    decode: (data) => ({
      entityClass: data.entityClass ?? undefined,
      entityId: Guid.fromJSON(data.entityId) ?? undefined,
      context: data.context ?? undefined,
    }),
    encode: (topic) => ({
      entityClass: topic.entityClass,
      entityId: topic.entityId ? topic.entityId.toJSON() : undefined,
      context: topic.context,
    }),
  });
}
